import { Stack } from '@mui/material'
import { useListEquipmentsQuery } from '../../../store/equipments/equipments.api'
import EquipmentListItem from './equipment-list-item/equipment-list-item.component'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../store'
import { setSelectedEquipment } from '../../../store/equipments/equipments.slice'
import {
  EquipmentResponse,
  EquipmentType,
} from '../../../store/equipments/equipments.types'
import { useEffect, useMemo } from 'react'

interface Tractors {
  tractors: EquipmentResponse[]
  selectedTractor: EquipmentResponse
  handleEquipmentSelect: (tractor: EquipmentResponse) => void
}

interface Trailers {
  trailers: EquipmentResponse[]
  selectedTrailer: EquipmentResponse
  handleEquipmentSelect: (trailer: EquipmentResponse) => void
}

interface EquipmentListProps {
  type: EquipmentType
  isActive?: boolean
}

const Tractors = ({
  tractors,
  selectedTractor,
  handleEquipmentSelect,
}: Tractors) => {
  return (
    <>
      {tractors.map((tractor) => (
        <EquipmentListItem
          key={tractor.equipmentId}
          equipment={tractor}
          isSelected={tractor.equipmentId === selectedTractor.equipmentId}
          handleEquipmentSelect={() => handleEquipmentSelect(tractor)}
        />
      ))}
    </>
  )
}

const Trailers = ({
  trailers,
  selectedTrailer,
  handleEquipmentSelect,
}: Trailers) => {
  return (
    <>
      {trailers.map((trailer) => (
        <EquipmentListItem
          key={trailer.equipmentId}
          equipment={trailer}
          isSelected={trailer.equipmentId === selectedTrailer.equipmentId}
          handleEquipmentSelect={() => handleEquipmentSelect(trailer)}
        />
      ))}
    </>
  )
}

const EquipmentList = ({ type, isActive = true }: EquipmentListProps) => {
  const { data: equipments } = useListEquipmentsQuery()

  const dispatch = useDispatch()

  const selectedEquipment = useSelector(
    (state: RootState) => state.equipments.selectedEquipment
  )

  const tractors = useMemo(
    () =>
      equipments?.filter((equipment) => equipment.equipmentType === 'TRACTOR') ??
      [],
    [equipments]
  )

  const trailers = useMemo(
    () =>
      equipments?.filter((equipment) => equipment.equipmentType === 'TRAILER') ??
      [],
    [equipments]
  )

  const handleTractorSelect = (tractor: EquipmentResponse) => {
    dispatch(setSelectedEquipment({ equipment: tractor }))
  }

  const handleTrailerSelect = (trailer: EquipmentResponse) => {
    dispatch(setSelectedEquipment({ equipment: trailer }))
  }

  const filteredEquipments = type === 'TRACTOR' ? tractors : trailers

  useEffect(() => {
    if (!isActive || !filteredEquipments.length) return

    const isCurrentInList = filteredEquipments.some(
      (equipment) => equipment.equipmentId === selectedEquipment.equipmentId
    )
    const matchesType = selectedEquipment.equipmentType === type

    if (!isCurrentInList || !matchesType) {
      dispatch(setSelectedEquipment({ equipment: filteredEquipments[0] }))
    }
  }, [
    dispatch,
    filteredEquipments,
    isActive,
    selectedEquipment.equipmentId,
    selectedEquipment.equipmentType,
    type,
  ])

  return (
    <Stack spacing={1.5} marginTop={'12px'}>
      {type === 'TRACTOR' && (
        <Tractors
          tractors={tractors}
          selectedTractor={selectedEquipment}
          handleEquipmentSelect={handleTractorSelect}
        />
      )}
      {type === 'TRAILER' && (
        <Trailers
          trailers={trailers}
          selectedTrailer={selectedEquipment}
          handleEquipmentSelect={handleTrailerSelect}
        />
      )}
    </Stack>
  )
}

export default EquipmentList
